import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BottomSheet } from '@shared/ui/adaptive/BottomSheet';
import { useMobileShell } from '@shared/shell';
import { useAuth } from '@shared/auth/AuthContext';
import { useActiveSuite } from '../useActiveSuite';
import { allSuites } from '@suites/registry';
import { SuiteIcon } from '@shared/icons';
import { useRecentPages } from '@shared/activity';
import { useTheme } from '@shared/theme/ThemeContext';
import { Sun, Moon, LogOut } from 'lucide-react';
import { cn } from '@shared/lib/utils';
import { Separator } from '@shared/ui/separator';

export const MobileLauncherSheet: React.FC = () => {
  const { launcherOpen, closeLauncher } = useMobileShell();
  const { user, hasPermission, logout } = useAuth();
  const activeSuite = useActiveSuite();
  const { theme, toggleTheme } = useTheme();
  const recentPages = useRecentPages();
  const navigate = useNavigate();

  const permittedSuites = allSuites
    .filter((s) => s.id !== 'inventory' && s.id !== 'crm')
    .filter((suite) =>
      suite.requiredPermission ? hasPermission(suite.requiredPermission) : true
    );

  const handleNavigate = (path: string) => {
    closeLauncher();
    navigate(path);
  };

  const handleLogout = async () => {
    closeLauncher();
    await logout();
  };

  return (
    <BottomSheet
      open={launcherOpen}
      onOpenChange={(open) => !open && closeLauncher()}
      title="Helios Launcher & Hub"
      description={user?.email ? `Signed in as ${user.email}` : undefined}
    >
      <div className="flex flex-col gap-5 pb-6">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2.5">
            Suites & Applications
          </h3>
          <div className="grid grid-cols-3 gap-2">
            {permittedSuites.map((suite) => {
              const isActive = activeSuite?.id === suite.id;
              return (
                <button
                  key={suite.id}
                  type="button"
                  onClick={() => handleNavigate(suite.basePath)}
                  className={cn(
                    'pressable flex flex-col items-center justify-center gap-1.5 rounded-xl p-3 text-center transition-colors border border-border/50 bg-card hover:bg-accent/60',
                    isActive &&
                      'bg-primary/10 border-primary/40 font-semibold ring-1 ring-primary/40'
                  )}
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
                    <SuiteIcon name={suite.icon} className="h-5 w-5" />
                  </div>
                  <span className="text-[11px] leading-tight line-clamp-2 text-foreground">
                    {suite.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {activeSuite && activeSuite.nav && activeSuite.nav.length > 0 && (
          <div>
            <Separator className="my-2" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              {activeSuite.name} Pages
            </h3>
            <div className="flex flex-col gap-1 max-h-48 overflow-y-auto pr-1">
              {activeSuite.nav.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => handleNavigate(item.path)}
                  className="pressable flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm hover:bg-accent/60 transition-colors"
                >
                  <SuiteIcon
                    name={item.icon || 'file'}
                    className="h-4 w-4 text-muted-foreground shrink-0"
                  />
                  <span className="truncate flex-1 font-medium">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {recentPages.length > 0 && (
          <div>
            <Separator className="my-2" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Recent Activity
            </h3>
            <div className="flex flex-col gap-1">
              {recentPages.slice(0, 5).map((page) => (
                <button
                  key={page.path}
                  type="button"
                  onClick={() => handleNavigate(page.path)}
                  className="pressable flex items-center justify-between px-3 py-2 rounded-lg text-left text-sm hover:bg-accent/60 transition-colors"
                >
                  <span className="truncate font-medium text-foreground">{page.title}</span>
                  <span className="text-xs text-muted-foreground shrink-0 ml-2">Open</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <Separator className="my-2" />
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            className="pressable flex items-center justify-between px-3 py-2.5 rounded-lg border border-border/50 bg-card hover:bg-accent/60 text-sm font-medium transition-colors"
          >
            <div className="flex items-center gap-2">
              {theme === 'dark' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
              <span>Theme: {theme === 'dark' ? 'Dark' : 'Light'}</span>
            </div>
            <span className="text-xs text-muted-foreground">Switch</span>
          </button>

          <button
            type="button"
            onClick={handleLogout}
            className="pressable flex items-center justify-between px-3 py-2.5 rounded-lg border border-destructive/30 bg-destructive/10 text-destructive hover:bg-destructive/20 text-sm font-medium transition-colors"
          >
            <div className="flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              <span>Sign Out</span>
            </div>
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};
